import { useState, useEffect, useCallback } from 'react'
import {
  BarChart3,
  Users,
  Trophy,
  Clock,
  ChevronDown,
  Loader2,
  TrendingDown,
  Timer,
  AlertCircle,
} from 'lucide-react'
import { getAdminHunts, getHuntAnalytics } from '../api'
import type { AdminHunt, HuntAnalytics } from '../api'

function StatCard({
  label,
  value,
  sub,
  icon: Icon,
  highlight,
}: {
  label: string
  value: string | number
  sub?: string
  icon: React.ComponentType<{ className?: string }>
  highlight?: boolean
}) {
  return (
    <div className={`rounded-xl border p-5 shadow-sm ${highlight ? 'border-amber-200 bg-amber-50' : 'border-stone-200 bg-white'}`}>
      <div className={`mb-3 flex h-10 w-10 items-center justify-center rounded-lg ${highlight ? 'bg-amber-100' : 'bg-amber-50'}`}>
        <Icon className="h-5 w-5 text-[#f59e0b]" />
      </div>
      <p className="text-2xl font-bold text-stone-900">{value}</p>
      <p className="text-sm text-stone-500">{label}</p>
      {sub && <p className="mt-1 text-xs text-stone-400">{sub}</p>}
    </div>
  )
}

function DurationBar({ label, count, max }: { label: string; count: number; max: number }) {
  const pct = max > 0 ? (count / max) * 100 : 0
  return (
    <div className="flex items-center gap-3">
      <span className="w-20 shrink-0 text-right text-xs text-stone-500">{label}</span>
      <div className="flex-1 h-5 rounded-full bg-stone-100 overflow-hidden">
        <div
          className="h-full rounded-full bg-gradient-to-r from-[#78350f] to-[#f59e0b] transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="w-6 text-xs font-medium text-stone-700">{count}</span>
    </div>
  )
}

export default function Analytics() {
  const [hunts, setHunts] = useState<AdminHunt[]>([])
  const [selectedHuntId, setSelectedHuntId] = useState<string | null>(null)
  const [analytics, setAnalytics] = useState<HuntAnalytics | null>(null)
  const [loadingHunts, setLoadingHunts] = useState(true)
  const [loadingAnalytics, setLoadingAnalytics] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadHunts = useCallback(async () => {
    try {
      setLoadingHunts(true)
      const data = await getAdminHunts()
      setHunts(data)
      if (data.length > 0) setSelectedHuntId(data[0].id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load hunts')
    } finally {
      setLoadingHunts(false)
    }
  }, [])

  useEffect(() => { loadHunts() }, [loadHunts])

  useEffect(() => {
    if (!selectedHuntId) return
    async function loadAnalytics() {
      try {
        setLoadingAnalytics(true)
        setError(null)
        const data = await getHuntAnalytics(selectedHuntId!)
        setAnalytics(data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load analytics')
        setAnalytics(null)
      } finally {
        setLoadingAnalytics(false)
      }
    }
    loadAnalytics()
  }, [selectedHuntId])

  if (loadingHunts) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#f59e0b]" />
      </div>
    )
  }

  if (hunts.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <BarChart3 className="mb-4 h-12 w-12 text-stone-300" />
        <h2 className="text-lg font-semibold text-stone-700">No hunts to analyze</h2>
        <p className="text-sm text-stone-500">Create a hunt first to see analytics.</p>
      </div>
    )
  }

  const maxBucket = analytics
    ? Math.max(...analytics.duration_buckets.map((b) => b.count), 1)
    : 1

  const durationDiff =
    analytics?.average_duration_minutes && analytics?.estimated_duration_minutes
      ? Math.round(analytics.average_duration_minutes - analytics.estimated_duration_minutes)
      : null

  return (
    <div className="p-8 max-w-4xl">
      <div className="mb-8 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-stone-900">Analytics</h1>
        <div className="relative">
          <select
            value={selectedHuntId || ''}
            onChange={(e) => setSelectedHuntId(e.target.value)}
            className="appearance-none rounded-lg border border-stone-200 bg-white py-2 pl-4 pr-10 text-sm font-medium text-stone-700 shadow-sm focus:border-[#f59e0b] focus:outline-none focus:ring-2 focus:ring-[#f59e0b]/20"
          >
            {hunts.map((hunt) => (
              <option key={hunt.id} value={hunt.id}>{hunt.title}</option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
        </div>
      </div>

      {error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-center gap-2">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {loadingAnalytics ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-[#f59e0b]" />
        </div>
      ) : analytics ? (
        <div className="space-y-6">
          {/* Participation stats */}
          <div className="grid grid-cols-3 gap-4">
            <StatCard label="Total Starts" value={analytics.total_starts} icon={Users} />
            <StatCard label="Completions" value={analytics.total_completions} icon={Trophy} />
            <StatCard
              label="Completion Rate"
              value={`${analytics.completion_rate.toFixed(1)}%`}
              icon={BarChart3}
            />
          </div>

          {/* Timing section */}
          <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm">
            <h2 className="mb-4 text-lg font-semibold text-stone-900 flex items-center gap-2">
              <Timer className="h-5 w-5 text-[#f59e0b]" />
              Completion Times
            </h2>

            {analytics.total_completions === 0 ? (
              <p className="text-sm text-stone-400 py-4 text-center">No completions yet — timing data will appear here.</p>
            ) : (
              <>
                {/* Timing stat grid */}
                <div className="grid grid-cols-4 gap-3 mb-6">
                  {[
                    { label: 'Average', value: analytics.average_duration_minutes },
                    { label: 'Median', value: analytics.median_duration_minutes },
                    { label: 'Fastest', value: analytics.min_duration_minutes },
                    { label: 'Slowest', value: analytics.max_duration_minutes },
                  ].map(({ label, value }) => (
                    <div key={label} className="rounded-lg bg-stone-50 border border-stone-100 p-3 text-center">
                      <p className="text-xl font-bold text-stone-900">
                        {value ? `${Math.round(value)} min` : '—'}
                      </p>
                      <p className="text-xs text-stone-500 mt-0.5">{label}</p>
                    </div>
                  ))}
                </div>

                {/* Estimate vs actual */}
                {durationDiff !== null && (
                  <div className={`mb-6 flex items-start gap-3 rounded-lg px-4 py-3 text-sm ${
                    Math.abs(durationDiff) <= 10
                      ? 'bg-green-50 border border-green-200 text-green-800'
                      : 'bg-amber-50 border border-amber-200 text-amber-800'
                  }`}>
                    <Clock className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>
                      Estimated: <strong>{analytics.estimated_duration_minutes} min</strong>
                      {' · '}
                      Actual avg: <strong>{Math.round(analytics.average_duration_minutes!)} min</strong>
                      {' · '}
                      {durationDiff > 0
                        ? `hunters take ${durationDiff} min longer than estimated`
                        : durationDiff < 0
                        ? `hunters finish ${Math.abs(durationDiff)} min faster than estimated`
                        : 'estimate is spot on'}
                    </span>
                  </div>
                )}

                {/* Duration distribution */}
                {analytics.duration_buckets.some((b) => b.count > 0) && (
                  <div>
                    <p className="text-xs font-medium text-stone-500 mb-3 uppercase tracking-wide">Distribution</p>
                    <div className="space-y-2">
                      {analytics.duration_buckets.map((b) => (
                        <DurationBar key={b.label} label={b.label} count={b.count} max={maxBucket} />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Suggested estimate update */}
            {analytics.median_duration_minutes && analytics.estimated_duration_minutes &&
             Math.abs(analytics.median_duration_minutes - analytics.estimated_duration_minutes) > 15 && (
              <div className="mt-4 rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-800 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 shrink-0" />
                Based on real data, consider updating your estimated duration to{' '}
                <strong>{Math.round(analytics.median_duration_minutes / 5) * 5} min</strong>{' '}
                (current median).
              </div>
            )}
          </div>

          {/* Waypoint funnel */}
          {analytics.waypoint_stats.length > 0 && (
            <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm">
              <h2 className="mb-4 text-lg font-semibold text-stone-900">Waypoint Funnel</h2>
              <div className="space-y-3">
                {analytics.waypoint_stats
                  .sort((a, b) => a.order_index - b.order_index)
                  .map((wp) => {
                    const maxCheckins = Math.max(
                      ...analytics.waypoint_stats.map((w) => w.checkins),
                      1
                    )
                    const barWidth = (wp.checkins / maxCheckins) * 100
                    return (
                      <div key={wp.waypoint_id} className="flex items-center gap-4">
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#78350f] text-xs font-bold text-white">
                          {wp.order_index + 1}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="mb-1 flex items-center justify-between">
                            <span className="truncate text-sm font-medium text-stone-700">
                              {wp.place_name || `Waypoint ${wp.order_index + 1}`}
                            </span>
                            <div className="flex items-center gap-3 text-xs text-stone-500 shrink-0">
                              {wp.avg_minutes_to_reach !== undefined && wp.avg_minutes_to_reach !== null && (
                                <span className="flex items-center gap-1 text-stone-400">
                                  <Clock className="h-3 w-3" />
                                  ~{wp.avg_minutes_to_reach} min
                                </span>
                              )}
                              <span>{wp.checkins} check-ins</span>
                              {wp.drop_off_rate > 0 && (
                                <span className="flex items-center gap-0.5 text-red-500">
                                  <TrendingDown className="h-3 w-3" />
                                  {wp.drop_off_rate.toFixed(0)}%
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="h-2 overflow-hidden rounded-full bg-stone-100">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-[#78350f] to-[#f59e0b]"
                              style={{ width: `${barWidth}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    )
                  })}
              </div>
            </div>
          )}
        </div>
      ) : null}
    </div>
  )
}
